import { FormattedMessage } from 'react-intl';
import { capitalize, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { FORM_ACTION, ReadMoreText } from '@ui-framework/index';
import { IconLibrary, PermissionButton } from '@product-framework/index';
import SpecialSchedule from '../../utils/SpecialSchedule';

// This helps add the "th" in 15th
dayjs.extend(advancedFormat);

function DeleteMessage({ nameProp }) {
  return (
    <Typography>
      <FormattedMessage
        id="DO_YOU_WANT_TO_DELETE_THE_SPECIAL_SCHEDULE"
        defaultMessage="Do you want to delete the special schedule"
      />{' '}
      <Typography
        color="primary"
        sx={{ fontWeight: 500 }}
        variant="body"
        component="span">
        {nameProp}
      </Typography>
      ?
    </Typography>
  );
}

function buttonActions(original, props, accessorFunctionsAdditional) {
  const {
    deleteActionDialog,
    refreshListView,
    params,
    formatMessage,
    readOnly,
    locationId = null,
    mode,
    navigate,
  } = accessorFunctionsAdditional;

  const locationUrl = locationId ? `/locations/${locationId}` : '';

  return (
    <Stack direction="row" spacing={2}>
      <PermissionButton
        permissionName={
          readOnly
            ? 'CAN_VIEW_SP_AVAILABILITY_SPECIAL_SCHEDULE'
            : 'CAN_UPDATE_SP_AVAILABILITY_SPECIAL_SCHEDULE'
        }
        disabled={mode === FORM_ACTION.EDIT}
        size="small"
        variant={readOnly ? 'contained' : 'outlined'}
        startIcon={readOnly ? <IconLibrary.View /> : <IconLibrary.Edit />}
        onClick={() => {
          navigate(
            `/serviceproviders/edit/${params?.pageId}${locationUrl}/availability/special-schedules/${original?.id}/edit`,
          );
        }}>
        {readOnly ? (
          <FormattedMessage id="VIEW" defaultMessage="View" />
        ) : (
          <FormattedMessage id="EDIT" defaultMessage="Edit" />
        )}
      </PermissionButton>
      <PermissionButton
        permissionName="CAN_DELETE_SP_AVAILABILITY_SPECIAL_SCHEDULE"
        disabled={mode === FORM_ACTION.EDIT}
        size="small"
        variant="outlined"
        color="error"
        startIcon={<IconLibrary.Delete />}
        onClick={() => {
          deleteActionDialog.init({
            message: <DeleteMessage nameProp={original?.name || ''} />,
            buttons: [
              {
                label: (
                  <FormattedMessage
                    id="DELETE_SCHEDULE"
                    defaultMessage="Delete Schedule"
                  />
                ),
                onClick: async () => {
                  deleteActionDialog?.updateMessage(
                    <Typography>
                      <FormattedMessage
                        id="DELETING_SCHEDULE"
                        defaultMessage="Deleting Schedule"
                      />
                      ...
                    </Typography>,
                  );
                  const response = await SpecialSchedule.delete({
                    params,
                    specialScheduleId: original?.id,
                    locationId,
                  });
                  if (response) {
                    params.snackbar.showMessage(
                      formatMessage({
                        id: 'SCHEDULE_WAS_DELETED_SUCCESSFULLY',
                        defaultMessage: 'Schedule was deleted successfully',
                      }),
                    );
                    refreshListView();
                    deleteActionDialog.close();
                  } else {
                    deleteActionDialog?.updateMessage(
                      <DeleteMessage nameProp={original?.name || ''} />,
                    );
                  }
                },
                variant: 'contained',
                color: 'error',
                Icon: IconLibrary.Delete,
              },
              {
                label: <FormattedMessage id="CLOSE" defaultMessage="Close" />,
                onClick: () => {
                  deleteActionDialog.close();
                },
                Icon: IconLibrary.Cancel,
                variant: 'outlined',
              },
            ],
            width: 450,
            Icon: IconLibrary.DefaultSchedule,
            title: (
              <FormattedMessage
                id="DELETE_SCHEDULE"
                defaultMessage="Delete Schedule"
              />
            ),
          });
          deleteActionDialog.open();
        }}>
        <FormattedMessage id="DELETE" defaultMessage="Delete" />
      </PermissionButton>
    </Stack>
  );
}

function name(original) {
  return original?.name || '';
}

function recurrentText(original) {
  let textToDisplay = capitalize(original?.recurrentText || '');
  if (!original?.recurrentText && original.recurrenceEnabled === false) {
    if (!original?.endDate) {
      textToDisplay = `Once on ${dayjs(original?.startDate).format('MMMM Do YYYY')}`;
    } else {
      const startDate = dayjs(original?.startDate).format('MMMM Do YYYY');
      const endDate = dayjs(original?.endDate).format('MMMM Do YYYY');
      textToDisplay = `Once from ${startDate} to ${endDate}`;
    }
  }
  return <ReadMoreText maxChars={100}>{textToDisplay}</ReadMoreText>;
}

export { name, buttonActions, recurrentText };
